/* The rule sheet's CHROME — the scrim it stands on, the pane it stands in, its head and its foot.
 *
 * `Builder planu - modal - progresja`, markup lines 585–700 and script 2640–3364: `pgScrim`,
 * `pgWrap`, `pane3`, `pane3Scroll`, and the foot bar under them. The three boxes INSIDE it — the
 * sentence, the matrix and the trajectory preview — carry their own stylesheets, because the 1041
 * line `Progression.module.css` this slice used to have is exactly how near-duplicate `.cell` and
 * `.toggle` blocks drifted between surfaces.
 *
 * ── Where it docks, and why one declaration covers both hosts ───────────────────────────────
 * The pane is portalled into an empty box of `DayForm`'s, and resolves against `.form` — the whole
 * day, its head and foot included, which is what the mock covers. It used to sit inside `.stage`
 * and leave the day's own chrome showing above and below; that deviation is withdrawn, and the day
 * behind the scrim is `inert` besides (see `ProgressionSheet.tsx`).
 *
 * The pane covers the WHOLE box in both hosts — a deliberate deviation from the mock, owner's call of
 * 12 October 2026. The mock's `pgWrap` takes half the modal with a 640px floor, and an eight-cycle
 * phase needs about 655px of matrix: measured on a live plan, every window narrower than 1712px
 * clipped the last cycle, and the „TU JEST KLIENT" centring slid the grid sideways on each open. In
 * the 574px panel nothing changes, because half of that box was already the whole of it. With the
 * day fully covered there is no edge left to light, so the mock's hairline and `-32px 0 80px` cast
 * went with the half-width; the scrim stays, because it is what the day dims into while the pane
 * slides in.
 *
 * ── The accent appears TWICE on this surface ────────────────────────────────────────────────
 * A pinned cell and the focus ring. Nothing else — not the apply button, which takes the ink fill
 * the mock draws, and not the family hues, which are their own tokens. The glow ban is absolute
 * outside `shared/ui/emberButton`, and every shadow here is neutral.
 */

/* ── The scrim ───────────────────────────────────────────────────────────────────────────── */

/* The mock's `pgScrim`: `inset: 0`, a neutral dim, `blur(2px)`. It is NOT a glass surface — see the
 * token's own note. It is a sibling of the pane rather than its parent, so a press on the day behind
 * it is caught here and closes the sheet without the pane having to stop propagation. */
.scrim {
  position: absolute;
  z-index: 1;
  inset: 0;
  background: var(--scrim);
  backdrop-filter: var(--blur-scrim-soft);
  animation: sheet-scrim-in var(--dur-fade) var(--ease-default) both;
}

/* ── The pane ────────────────────────────────────────────────────────────────────────────── */

.sheet {
  position: absolute;
  z-index: 2;
  display: flex;
  inset: 0;
  flex-direction: column;
  min-height: 0;
  background: var(--bg-sunken);

  /* `body` sets `--lh-relaxed` app-wide, which is prose leading; this pane is forty micro-labels
   * deep and inheriting it pushes every row apart. The same declaration `DayPanelHost` carries. */
  line-height: var(--lh-auto);
  animation: sheet-slide-in var(--dur-panel) var(--ease-default) both;
}

/* The mock's `pgSlide`: 28px in, fading. Transform and opacity only — nothing here lays the day
 * behind it out again. */
@keyframes sheet-slide-in {
  from {
    opacity: 0;
    transform: translateX(var(--space-7));
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes sheet-scrim-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* The preference cannot be read from a Motion animation, but these two ARE CSS, so the media block
 * reaches them. Held, not delayed: a zero duration on a `both`-filled entrance leaves the element at
 * its `from` keyframe for the whole delay, which is an invisible sheet. */
@media (prefers-reduced-motion: reduce) {
  .sheet,
  .scrim {
    animation: none;
  }
}

/* The mock's `pane3Scroll`. The pane FITS OR SCROLLS and never clips: at 620px of viewport the
 * matrix and the trajectory together are taller than the day, and this is the box that answers. */
.scroll {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-5px);
  min-height: 0;
  padding: var(--space-2) var(--space-14px) var(--space-6px);
  overflow: hidden auto;
}

/* ── The head ────────────────────────────────────────────────────────────────────────────── */

.head {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.headTitles {
  display: flex;
  flex-direction: column;
  gap: var(--space-1px);
  min-width: 0;
}

.caption {
  overflow: hidden;
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-eyebrow);
  text-transform: uppercase;
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.title {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-title);
  color: var(--text);
  white-space: nowrap;
}

.spring {
  flex: 1;
}

/* The column chips — the mock's `colCards3`. One per column the movement measures; the dot carries
 * the family hue where that column holds a rule, and the WORD beside it is never the only channel:
 * every chip's accessible name says what its state is. */
.columns {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
  flex-wrap: wrap;
}

.column {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  height: var(--h-prog-chip);
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

/* The mock's `colCards3`, which colours a chip by the FAMILY of the rule on that column rather than
 * by whether it is chosen: an unchosen ruled column keeps the hue on its ink and its edge, and the
 * chosen one is filled with it. A column carrying no rule takes plain ink either way — the neutral
 * default this app's accent rule asks for. */
.column[data-family='static'] {
  color: var(--prog-sheet-static-ink);
  border-color: var(--prog-sheet-static);
}

.column[data-family='fromResult'] {
  color: var(--prog-sheet-result-ink);
  border-color: var(--prog-sheet-result);
}

.column[aria-checked='true'] {
  color: var(--bg-card);
  background: var(--text);
  border-color: var(--text);
}

.column[aria-checked='true'][data-family='static'] {
  background: var(--prog-sheet-static);
  border-color: var(--prog-sheet-static);
}

.column[aria-checked='true'][data-family='fromResult'] {
  background: var(--prog-sheet-result);
  border-color: var(--prog-sheet-result);
}

.column:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.columnDot {
  display: block;
  width: var(--space-6px);
  height: var(--space-6px);
  background: var(--prog-sheet-flat);
  border-radius: var(--r-circle);
}

.column[aria-checked='true'] .columnDot {
  background: var(--bg-card);
}

.columnDot[data-family='static'] {
  background: var(--prog-sheet-static);
}

.columnDot[data-family='fromResult'] {
  background: var(--prog-sheet-result);
}

.columnDot[data-family='gate'] {
  background: var(--text);
}

/* The mock's `helpBtn` — a 24px square that toggles every hint on the surface at once. It is a real
 * toggle rather than a tooltip: the hints are a paragraph each, and a trainer who knows the rule
 * vocabulary wants them gone for good. */
.help {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--h-prog-help);
  height: var(--h-prog-help);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-7px);
}

.help[aria-pressed='true'] {
  color: var(--bg-card);
  background: var(--text);
  border-color: var(--text);
}

.help:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* ── Section labels, shared by the matrix header and the preview header ──────────────────── */

.sectionRow {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  min-height: var(--h-prog-chip);
}

.sectionLabel {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-group);
  text-transform: uppercase;
  color: var(--text-2);
  white-space: nowrap;
}

/* Every hint on the surface — the legend, the rounding note, the narration — wears this and is
 * removed from the DOM, not hidden, when the `?` is off. A hidden hint still read out is a paragraph
 * a screen reader hears that a sighted trainer has switched off. */
.hint {
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

/* The one line that is NOT a hint: it says why the grid below is drawn on a different column from
 * the one the chips have selected, so it is printed whether or not the `?` is on. */
.note {
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

.legend {
  display: inline-flex;
  gap: var(--space-10px);
  align-items: center;
  flex-wrap: wrap;
}

.legendItem {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
}

/* The swatch is the cell's own ring, at 16x11 — the mock's `l.sw`. Redundant with the word beside
 * it on purpose: the legend is the key that makes the grid's colours readable, so the key itself
 * must not depend on colour. */
.swatch {
  display: block;
  flex: none;
  width: var(--space-4);
  height: var(--space-11px);
  border: var(--border-thick) solid var(--prog-sheet-base);
  border-radius: var(--radius-4px);
}

.swatch[data-state='certain'] {
  border-color: var(--prog-sheet-static);
}

.swatch[data-state='estimate'] {
  background: var(--prog-sheet-result-wash);
  border-style: dashed;
  border-color: var(--prog-sheet-result);
}

.swatch[data-state='derived'] {
  border-style: dashed;
  border-color: var(--border-2);
}

.swatch[data-state='manual'] {
  background: var(--prog-sheet-manual-wash);
  border-color: var(--prog-sheet-manual);
}

.swatch[data-state='flat'] {
  border-color: var(--prog-sheet-flat);
}

.swatch[data-state='base'] {
  background: var(--bg-card);
}

/* ── The foot ────────────────────────────────────────────────────────────────────────────── */

/* `flex: none` and outside the scroller, so the two acts that END the session are reachable without
 * scrolling however tall the matrix grows. */
.foot {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-10px) var(--space-4) var(--space-14px);
  background: var(--bg-sunken);
  border-top: var(--border-hairline) solid var(--border);
}

.kbdHint {
  display: inline-flex;
  gap: var(--space-2);
  align-items: center;
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-medium);
  color: var(--text-2);
  white-space: nowrap;
}

.kbd {
  display: inline-grid;
  place-items: center;
  height: var(--space-5);
  padding: var(--space-0) var(--space-6px);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  font-style: normal;
  color: var(--text-2);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-5px);
}

/* ── The foot's two buttons ──────────────────────────────────────────────────────────────── */

/* `ghostButton` and `inkButton` carry the TREATMENT — the fill, the hairline, the refused ink, the
 * focus ring — and no geometry at all, deliberately, so each surface sizes them to its own canon.
 * The mock's are 26px for a ghost and 28px for the primary, at radius 9.
 *
 * The primary is the INK button, not the ember one: the mock draws a white fill on `#111` ink, this
 * app's accent is a scalpel, and „Zastosuj do draftu" is one press among several on a sheet that
 * already spends its accent on a pinned cell and the focus ring.
 *
 * The ghost's GROUND is the surface's own declaration (see `ghostButton.module.css`), and this foot
 * forgot to make it — leaving both ghosts on the UA's `ButtonFace`, which is no token at all and
 * differs between the two themes. In Flame it lands within 1,02:1 of `--bg-chip`, so the refused
 * fill the shared treatment paints was invisible on this foot and on this foot alone. Transparent
 * over the foot's `--bg-sunken` is the reading the client detail's tabs already give this control. */
.footButton {
  background: transparent;
  height: var(--space-26px);
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  white-space: nowrap;
  border-radius: var(--radius-mark);
}

.footPrimary {
  height: var(--space-7);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  border-radius: var(--radius-mark);
}
